import { LotCorrectionFields, validCorrection } from '../../components/LotCorrectionFields.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { checkmarkCircleOutline, trashOutline, createOutline, calendarOutline, optionsOutline } from 'ionicons/icons';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useEnqueueStockCommand,
  useMasters,
  useStockProjection,
} from '../../lib/data/queries.js';
import { EmptyHint, SectionHeader, palette } from '../../components/design.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { useHistory } from 'react-router-dom';
import {
  buildExpirationConsumeCommand,
  buildExpirationInventoryAdjustmentCommand,
} from './expirationStockCommand.js';
import {
  EXPIRATION_ALERT_LEAD_OPTIONS,
  EXPIRATION_REMINDER_LEAD_OPTIONS,
  formatExpirationLead,
  isWithinExpirationLead,
  normalizeExpirationPreferences,
  useExpirationPreferences,
  writeExpirationPreferences,
  type ExpirationPreferences,
} from '../../lib/haccp/expirationPreferences.js';
import { rescheduleExpirationNotifications } from '../../lib/notifications/expirationNotifier.js';
import { selectActionableExpirationAlerts } from '../../lib/haccp/expirationAlerts.js';
import { matchesExpirationSearch } from './expirationSearch.js';

interface Food {
  id: string;
  name: string;
  default_uom?: string;
  recipe_id?: string | null;
}

interface LotItem {
  id: string;
  food_id: string;
  product: string;
  uom: string;
  date: string;
  daysLeft: number;
  lot: string | null;
  qty: number;
  onHandQty: number;
  reservedQty: number;
}

type ActionKind = 'consumed' | 'waste' | 'qty' | 'date' | null;

export function ExpirationsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const history = useHistory();
  const enqueueStockCommand = useEnqueueStockCommand();
  const [presentToast] = useIonToast();
  const preferenceScope = profile?.active_business_id ?? null;
  const expirationPreferences = useExpirationPreferences(preferenceScope);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [lotSearch, setLotSearch] = useState('');
  const [draftPreferences, setDraftPreferences] = useState<ExpirationPreferences>(expirationPreferences);

  const { data: foods = [] } = useMasters<Food>('foods');
  const canonicalUomByFood = useMemo(() => Object.fromEntries(
    foods
      .filter((food) => typeof food.default_uom === 'string' && food.default_uom.trim() !== '')
      .map((food) => [food.id, food.default_uom!.trim()]),
  ), [foods]);
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });

  const today = Date.now();
  const foodById = new Map(foods.map((food) => [food.id, food]));
  const actionableBalances = selectActionableExpirationAlerts(
    stockProjection.balances
      .filter((balance) => balance.availableQty > 0 && !balance.closed && balance.expirationDate)
      .map((balance) => ({
        ...balance,
        id: balance.key,
        isPreparation: !!foodById.get(balance.foodId)?.recipe_id,
      })),
    today,
  );
  const items: LotItem[] = actionableBalances
    .map((balance) => {
      const food = foodById.get(balance.foodId);
      const days = Math.floor((Date.parse(balance.expirationDate!) - today) / 86_400_000);
      return {
        id: balance.key,
        food_id: balance.foodId,
        product: food?.name ?? balance.foodId.slice(0, 8),
        uom: balance.uom,
        date: balance.expirationDate!,
        daysLeft: days,
        lot: balance.batchLot,
        qty: balance.availableQty,
        onHandQty: balance.onHandQty,
        reservedQty: balance.reservedQty,
      };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);
  const visibleItems = items.filter((item) => (
    matchesExpirationSearch(item.product, item.lot, lotSearch)
  ));
  const relevantUomQuarantine = stockProjection.quarantine.filter((entry) => (
    entry.code === 'incompatible_uom'
    || entry.code === 'missing_uom'
    || entry.code === 'partial_stock_quantity'
  ));
  const uomQuarantineCount = selectActionableExpirationAlerts(
    relevantUomQuarantine.map((entry, index) => {
      const foodId = typeof entry.event.payload?.food_id === 'string'
        ? entry.event.payload.food_id
        : '';
      const expirationDate = typeof entry.event.payload?.expiration_date === 'string'
        ? entry.event.payload.expiration_date
        : null;
      return {
        entry,
        id: entry.eventId ?? `quarantine-${index}`,
        foodId,
        expirationDate,
        isPreparation: !!foodById.get(foodId)?.recipe_id,
      };
    }),
    today,
  ).length;

  // Conteggi per fasce
  const expired = items.filter((i) => i.daysLeft < 0).length;
  const alertWindow = items.filter((i) => (
    i.daysLeft >= 0
    && isWithinExpirationLead(i.date, expirationPreferences.alertLeadHours, today)
  )).length;
  const week = items.filter((i) => i.daysLeft >= 3 && i.daysLeft <= 7).length;

  const [actionItem, setActionItem] = useState<LotItem | null>(null);
  const [actionKind, setActionKind] = useState<ActionKind>(null);
  const [qtyInput, setQtyInput] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionCopies, setCorrectionCopies] = useState('0');
  const [dateInput, setDateInput] = useState('');
  const [confirmAlert, setConfirmAlert] = useState<{ item: LotItem; kind: 'consumed' | 'waste' } | null>(null);
  const slidingRef = useRef<HTMLIonItemSlidingElement | null>(null);

  function openAction(item: LotItem, kind: ActionKind): void {
    setCorrectionReason(''); setCorrectionCopies('0');
    setActionItem(item);
    setActionKind(kind);
    if (kind === 'qty') setQtyInput(String(item.qty));
    if (kind === 'date') setDateInput(item.date);
  }

  function printCorrectedLot(update: { date?: string; quantity?: number; uom?: string }): void {
    if (!actionItem || Number(correctionCopies) === 0) return;
    history.push('/haccp/labels', { lotCorrection: {
      id: crypto.randomUUID(), tenantId: profile?.active_tenant_id,
      businessId: profile?.active_business_id, foodId: actionItem.food_id,
      lot: actionItem.lot, copies: Number(correctionCopies), reason: correctionReason.trim(), ...update,
    } });
  }
  function closeModal(): void {
    setActionItem(null);
    setActionKind(null);
    setQtyInput('');
    setDateInput('');
  }

  function openSettings(): void {
    setDraftPreferences(expirationPreferences);
    setSettingsOpen(true);
  }

  function updateReminder(index: number, value: number): void {
    const next = [
      draftPreferences.reminderLeadHours[0] ?? 0,
      draftPreferences.reminderLeadHours[1] ?? 0,
    ];
    next[index] = value;
    setDraftPreferences({
      ...draftPreferences,
      reminderLeadHours: next,
    });
  }

  async function saveSettings(): Promise<void> {
    setSavingSettings(true);
    try {
      const normalized = normalizeExpirationPreferences(draftPreferences);
      await rescheduleExpirationNotifications(
        items.map((item) => ({ id: item.id, product: item.product, date: item.date })),
        normalized,
        preferenceScope,
      );
      writeExpirationPreferences(normalized, preferenceScope);
      setSettingsOpen(false);
      void presentToast({ message: 'Tempi degli avvisi aggiornati', duration: 2000, color: 'success' });
    } catch (err) {
      void presentToast({ message: toUserMessage(err), duration: 3000, color: 'danger' });
    } finally {
      setSavingSettings(false);
    }
  }

  async function emitStockOut(
    item: LotItem,
    qty: number,
    reason: 'consumed' | 'waste',
    closeLot: boolean,
  ): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      throw new Error('Nessuna sede attiva');
    }
    await enqueueStockCommand.mutateAsync(buildExpirationConsumeCommand({
      commandId: crypto.randomUUID(),
      tenantId: profile.active_tenant_id,
      businessId: profile.active_business_id,
      foodId: item.food_id,
      lot: item.lot,
      expirationDate: item.date,
      qty,
      uom: item.uom,
      reason,
      closeLot,
    }));
  }

  async function emitInventoryAdjustment(input: {
    item: LotItem;
    systemQty: number;
    countedQty: number;
    storageUnitId: string | null;
    unitCost: number | null;
    currency: string | null;
  }): Promise<void> {
    if (!profile?.active_tenant_id || !profile.active_business_id) {
      throw new Error('Nessuna sede attiva');
    }
    await enqueueStockCommand.mutateAsync(
      buildExpirationInventoryAdjustmentCommand({
        commandId: crypto.randomUUID(),
        tenantId: profile.active_tenant_id,
        businessId: profile.active_business_id,
        foodId: input.item.food_id,
        lot: input.item.lot,
        expirationDate: input.item.date,
        storageUnitId: input.storageUnitId,
        systemQty: input.systemQty,
        countedQty: input.countedQty,
        uom: input.item.uom,
        countedAt: new Date().toISOString(),
        reason: `Rettifica quantità: ${correctionReason.trim()}`,
        operatorName: profile.full_name ?? null,
        unitCost: input.unitCost,
        currency: input.currency ?? 'EUR',
      }),
    );
  }

  async function handleConfirmFullAction(item: LotItem, kind: 'consumed' | 'waste'): Promise<void> {
    try {
      if (item.reservedQty > 1e-9 || Math.abs(item.onHandQty - item.qty) > 1e-9) {
        throw new Error('Il lotto contiene quantità prenotate: libera prima le spedizioni in bozza');
      }
      await emitStockOut(item, item.onHandQty, kind, true);
      void presentToast({ message: kind === 'consumed' ? 'Lotto consumato e chiuso' : 'Lotto scaricato e chiuso come scarto', duration: 2000, color: 'success' });
    } catch (err) {
      void presentToast({ message: toUserMessage(err), duration: 3000, color: 'danger' });
    }
    setConfirmAlert(null);
  }

  async function handleQtyAdjust(): Promise<void> {
    if (!validCorrection(correctionReason, correctionCopies)) return;
    if (!actionItem) return;
    const liveBalance = stockProjection.balances.find(
      (balance) => balance.key === actionItem.id,
    );
    if (!liveBalance) {
      void presentToast({ message: 'La giacenza è cambiata: riapri la rettifica e riprova', duration: 2500, color: 'warning' });
      closeModal();
      return;
    }
    if (
      Math.abs(liveBalance.onHandQty - actionItem.onHandQty) > 1e-9
      || liveBalance.uom.trim().toLowerCase()
        !== actionItem.uom.trim().toLowerCase()
      || liveBalance.expirationDate !== actionItem.date
    ) {
      void presentToast({ message: 'La giacenza è cambiata: riapri la rettifica e riprova', duration: 2500, color: 'warning' });
      closeModal();
      return;
    }
    if (liveBalance.reservedQty > 1e-9) {
      void presentToast({ message: 'Libera prima le quantità prenotate nelle spedizioni in bozza', duration: 2500, color: 'warning' });
      return;
    }
    if (stockProjection.quarantine.some(
      ({ event }) => event.payload?.food_id === actionItem.food_id,
    )) {
      void presentToast({ message: 'Movimenti non coerenti: verifica la giacenza prima della rettifica', duration: 2500, color: 'warning' });
      return;
    }
    const newQty = Number(qtyInput.replace(',', '.'));
    if (!Number.isFinite(newQty) || newQty < 0) {
      void presentToast({ message: 'Quantità non valida', duration: 2000, color: 'warning' });
      return;
    }
    if (Math.abs(newQty - liveBalance.onHandQty) <= 1e-9) {
      void presentToast({ message: 'La quantità è invariata', duration: 2500, color: 'warning' });
      return;
    }
    try {
      await emitInventoryAdjustment({
        item: actionItem,
        systemQty: liveBalance.onHandQty,
        countedQty: newQty,
        storageUnitId: liveBalance.storageUnitId,
        unitCost: liveBalance.averageUnitCost,
        currency: liveBalance.valueCurrency,
      });
      printCorrectedLot({ quantity: newQty, uom: actionItem.uom });
      void presentToast({ message: `Quantità rettificata a ${newQty} ${actionItem.uom}`, duration: 2000, color: 'success' });
      closeModal();
    } catch (err) {
      void presentToast({ message: toUserMessage(err), duration: 3000, color: 'danger' });
    }
  }

  async function handleDateCorrection(): Promise<void> {
    if (
      !actionItem
      || !dateInput
      || !validCorrection(correctionReason, correctionCopies)
      || !profile?.active_tenant_id
      || !profile.active_business_id
    ) return;
    try {
      await enqueueStockCommand.mutateAsync({
        version: 1,
        command_id: crypto.randomUUID(),
        tenant_id: profile.active_tenant_id,
        movement_class: 'lot_state',
        economic_effect: 'stock_only',
        reason: `Correzione scadenza: ${correctionReason.trim()}`,
        business_id: profile.active_business_id,
        lines: [{
          food_id: actionItem.food_id,
          action: 'update_metadata',
          uom: actionItem.uom,
          batch_lot: actionItem.lot,
          expiration_date: dateInput,
          supplier_id: null,
          storage_unit_id: null,
          document_id: null,
          manual_entry: true,
          operator_name: profile.full_name ?? null,
        }],
      });
      printCorrectedLot({ date: dateInput });
      void rescheduleExpirationNotifications(
        items.map((item) => ({
          id: item.id,
          product: item.product,
          date: item.id === actionItem.id ? dateInput : item.date,
        })),
        expirationPreferences,
        preferenceScope,
      ).catch(() => undefined);
      void presentToast({ message: 'Data scadenza corretta', duration: 2000, color: 'success' });
      closeModal();
    } catch (err) {
      void presentToast({ message: toUserMessage(err), duration: 3000, color: 'danger' });
    }
  }

  function rowAccent(daysLeft: number): string {
    if (daysLeft < 0) return palette.danger;
    if (daysLeft <= 2) return palette.warn;
    if (daysLeft <= 7) return '#0d9488';
    return palette.textFaint;
  }

  function badgeBg(daysLeft: number): { bg: string; fg: string } {
    if (daysLeft < 0) return { bg: palette.dangerSoft, fg: palette.danger };
    if (daysLeft <= 2) return { bg: palette.warnSoft, fg: palette.warn };
    if (daysLeft <= 7) return { bg: '#ccfbf1', fg: '#0d9488' };
    return { bg: '#f1f5f9', fg: palette.textFaint };
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>{t('haccp.expirations')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        {/* Sintesi 3 colonne */}
        {items.length > 0 && (
          <div style={{ padding: '12px 16px 4px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { count: expired, label: 'Scaduti', tone: 'danger' as const },
              { count: alertWindow, label: `Entro ${formatExpirationLead(expirationPreferences.alertLeadHours)}`, tone: 'warn' as const },
              { count: week, label: 'Entro 7gg', tone: 'neutral' as const },
            ].map((s) => {
              const color = s.tone === 'danger' ? palette.danger : s.tone === 'warn' ? palette.warn : palette.textMuted;
              return (
                <div
                  key={s.label}
                  style={{
                    background: palette.card,
                    borderRadius: 12,
                    padding: '12px 8px',
                    textAlign: 'center',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 700, color }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: palette.textFaint, marginTop: 2 }}>{s.label}</div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ padding: '0 16px 24px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
              background: palette.card,
              borderRadius: 14,
              padding: '12px 14px',
              margin: items.length > 0 ? '8px 0 4px' : '12px 0 4px',
              boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: palette.text }}>
                Tempi degli avvisi
              </div>
              <div style={{ fontSize: 12, color: palette.textFaint, lineHeight: 1.45, marginTop: 2 }}>
                In app: {formatExpirationLead(expirationPreferences.alertLeadHours)} prima
                {' · '}Telefono: {expirationPreferences.reminderLeadHours.length > 0
                  ? expirationPreferences.reminderLeadHours.map(formatExpirationLead).join(' e ')
                  : 'disattivate'}
              </div>
            </div>
            <IonButton
              fill="outline"
              size="small"
              onClick={openSettings}
              aria-label="Modifica tempi degli avvisi scadenza"
              style={{ minHeight: 44, flexShrink: 0 }}
            >
              <IonIcon slot="start" icon={optionsOutline} />
              Modifica
            </IonButton>
          </div>

          <SectionHeader title="Lotti in scadenza" />

          {items.length > 0 && (
            <IonSearchbar
              placeholder="Cerca per prodotto o lotto"
              aria-label="Cerca nei lotti in scadenza per prodotto o numero di lotto"
              inputmode="search"
              value={lotSearch}
              onIonInput={(event) => setLotSearch(String(event.detail.value ?? ''))}
              style={{ padding: 0, marginBottom: 8 }}
            />
          )}

          {uomQuarantineCount > 0 && (
            <div
              role="alert"
              style={{
                background: palette.warnSoft,
                border: `1px solid ${palette.warn}`,
                borderRadius: 12,
                padding: '10px 12px',
                marginBottom: 8,
                fontSize: 12,
                color: palette.warn,
                lineHeight: 1.4,
              }}
            >
              <b>Movimenti in quarantena UdM: {uomQuarantineCount}</b>
              <br />
              Non sono inclusi nella giacenza perché l'unità di misura è
              incompatibile o incompleta.
            </div>
          )}

          {items.length > 0 && (
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 12,
              padding: '10px 12px',
              marginBottom: 8,
              fontSize: 12,
              color: '#1e40af',
              lineHeight: 1.4,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                💡 Come usare questa pagina
              </div>
              <div>
                <b>Swipe →</b> Finito (verde) o Scartato (rosso) per scaricare il lotto.
                <br />
                <b>← Swipe</b> Quantità o Data per rettifiche.
              </div>
            </div>
          )}

          {items.length === 0 && !isIntroDismissed('expirations', profile?.id ?? null, profile?.active_business_id ?? null) ? (
            <FeatureIntroBlock
              featureKey="expirations"
              icon="⏰"
              title="Scadenze in magazzino"
              description={
                <>
                  Qui vedi i <b>lotti dei tuoi alimenti in scadenza</b>: cosa
                  scade nei prossimi giorni, quale lotto è più vicino alla
                  data di TMC/scadenza, quanto ne resta in giacenza.
                  <br /><br />
                  I lotti si creano automaticamente quando carichi un{' '}
                  <b>DDT o una fattura</b>: l'AI estrae fornitore, articoli,
                  lotti e scadenze. Per iniziare a popolare questa schermata,
                  carica il primo documento da Documenti → Carica DDT/Fattura.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi sulla gestione delle scadenze in TrackEAT, mi puoi aiutare?"
              startLabel="Vai a Documenti"
              onStart={() => history.push('/documents/capture')}
              userId={profile?.id ?? null}
              businessId={profile?.active_business_id ?? null}
              flush
            />
          ) : items.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
              <EmptyHint>{t('common.empty')}</EmptyHint>
            </div>
          ) : visibleItems.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
              <EmptyHint>Nessun prodotto o lotto corrisponde alla ricerca.</EmptyHint>
            </div>
          ) : (
            <IonList style={{ background: 'transparent', padding: 0 }} lines="none">
              {visibleItems.map((it) => {
                const accent = rowAccent(it.daysLeft);
                const badge = badgeBg(it.daysLeft);
                return (
                  // Wrapper IonItemSliding con border-radius + overflow:hidden:
                  // così le IonItemOptions colorate che appaiono dietro durante
                  // lo swipe restano visivamente contenute nei limiti della
                  // card invece di sforare oltre i bordi arrotondati.
                  <IonItemSliding
                    key={it.id}
                    ref={it.id === actionItem?.id ? slidingRef : undefined}
                    style={{
                      borderRadius: 14,
                      overflow: 'hidden',
                      margin: '8px 0',
                      boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                      background: palette.card,
                    }}
                  >
                    <IonItemOptions side="start">
                      <IonItemOption
                        color="success"
                        onClick={() => { setConfirmAlert({ item: it, kind: 'consumed' }); }}
                      >
                        <IonIcon slot="icon-only" icon={checkmarkCircleOutline} />
                        Finito
                      </IonItemOption>
                      <IonItemOption
                        color="danger"
                        onClick={() => { setConfirmAlert({ item: it, kind: 'waste' }); }}
                      >
                        <IonIcon slot="icon-only" icon={trashOutline} />
                        Scartato
                      </IonItemOption>
                    </IonItemOptions>

                    <IonItem
                      style={{
                        '--background': palette.card,
                        '--padding-start': '14px',
                        '--inner-padding-end': '14px',
                        borderLeft: `4px solid ${accent}`,
                      } as React.CSSProperties}
                    >
                      <div style={{ flex: 1, padding: '4px 0' }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: palette.text }}>{it.product}</div>
                        <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>
                          {new Date(it.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {it.lot ? ` · lotto ${it.lot}` : ''} · {it.qty} {it.uom}
                        </div>
                      </div>
                      <div
                        slot="end"
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          fontSize: 12,
                          fontWeight: 700,
                          padding: '6px 10px',
                          borderRadius: 10,
                          minWidth: 56,
                          textAlign: 'center',
                        }}
                      >
                        {it.daysLeft < 0
                          ? 'Scaduto'
                          : it.daysLeft === 0
                          ? 'Oggi'
                          : `${it.daysLeft}gg`}
                      </div>
                    </IonItem>

                    <IonItemOptions side="end">
                      <IonItemOption color="primary" onClick={() => openAction(it, 'qty')}>
                        <IonIcon slot="icon-only" icon={createOutline} />
                        Quantità
                      </IonItemOption>
                      <IonItemOption color="tertiary" onClick={() => openAction(it, 'date')}>
                        <IonIcon slot="icon-only" icon={calendarOutline} />
                        Data
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </IonList>
          )}
        </div>

        <IonAlert
          isOpen={confirmAlert !== null}
          header={confirmAlert?.kind === 'consumed' ? 'Lotto consumato' : 'Lotto scartato'}
          message={
            confirmAlert
              ? `Registra ${confirmAlert.item.qty} ${confirmAlert.item.uom} di "${confirmAlert.item.product}" come ${confirmAlert.kind === 'consumed' ? 'consumato' : 'scarto'}?`
              : ''
          }
          buttons={[
            { text: 'Annulla', role: 'cancel', handler: () => setConfirmAlert(null) },
            {
              text: 'Conferma',
              handler: () => {
                if (confirmAlert) void handleConfirmFullAction(confirmAlert.item, confirmAlert.kind);
              },
            },
          ]}
          onDidDismiss={() => setConfirmAlert(null)}
        />

        <IonModal isOpen={actionKind === 'qty'} onDidDismiss={closeModal}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Rettifica quantità</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={closeModal}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {actionItem && (
              <>
                <p style={{ marginBottom: 8 }}>
                  <strong>{actionItem.product}</strong>
                  {actionItem.lot ? ` — lotto ${actionItem.lot}` : ''}
                </p>
                <p style={{ color: 'var(--ion-color-medium)', marginBottom: 16 }}>
                  Giacenza attuale: {actionItem.qty} {actionItem.uom}
                </p>
                <IonItem>
                  <IonLabel position="stacked">Nuova quantità ({actionItem.uom})</IonLabel>
                  <IonInput
                    type="number"
                    inputmode="decimal"
                    value={qtyInput}
                    onIonInput={(e) => setQtyInput(e.detail.value ?? '')}
                    placeholder="0"
                  />
                </IonItem>
                <LotCorrectionFields reason={correctionReason} copies={correctionCopies}
                  onReason={setCorrectionReason} onCopies={setCorrectionCopies} />
                <IonButton
                  expand="block"
                  style={{ marginTop: 24 }}
                  onClick={() => void handleQtyAdjust()}
                  disabled={enqueueStockCommand.isPending || !validCorrection(correctionReason, correctionCopies)}
                >
                  {Number(correctionCopies) > 0 ? `Salva e ristampa ${correctionCopies} etichette` : "Salva rettifica"}
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonModal isOpen={actionKind === 'date'} onDidDismiss={closeModal}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Correggi data scadenza</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={closeModal}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {actionItem && (
              <>
                <p style={{ marginBottom: 8 }}>
                  <strong>{actionItem.product}</strong>
                  {actionItem.lot ? ` — lotto ${actionItem.lot}` : ''}
                </p>
                <p style={{ color: 'var(--ion-color-medium)', marginBottom: 16 }}>
                  Data attuale: {actionItem.date}
                </p>
                <IonItem>
                  <IonLabel position="stacked">Nuova data scadenza</IonLabel>
                  <IonInput
                    type="date"
                    value={dateInput}
                    onIonInput={(e) => setDateInput(e.detail.value ?? '')}
                  />
                </IonItem>
                <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85em', marginTop: 12 }}>
                  Registra la nuova data come rettifica del lotto, conservando lo storico.
                </p>
                <LotCorrectionFields reason={correctionReason} copies={correctionCopies}
                  onReason={setCorrectionReason} onCopies={setCorrectionCopies} />
                <IonButton
                  expand="block"
                  style={{ marginTop: 24 }}
                  onClick={() => void handleDateCorrection()}
                  disabled={enqueueStockCommand.isPending || !dateInput || !validCorrection(correctionReason, correctionCopies)}
                >
                  {Number(correctionCopies) > 0 ? `Correggi e ristampa ${correctionCopies} etichette` : "Conferma correzione"}
                </IonButton>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonModal isOpen={settingsOpen} onDidDismiss={() => setSettingsOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Tempi degli avvisi</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSettingsOpen(false)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p style={{ marginTop: 0, color: palette.textMuted, lineHeight: 1.5 }}>
              Queste preferenze valgono per il locale corrente su questo dispositivo.
              Le date dei singoli lotti si correggono invece scorrendo la relativa riga verso sinistra e toccando <b>Data</b>.
            </p>

            <IonItem>
              <IonLabel position="stacked">Mostra l’avviso nell’app</IonLabel>
              <IonSelect
                value={draftPreferences.alertLeadHours}
                interface="popover"
                aria-label="Anticipo avviso nell'app"
                onIonChange={(event) => setDraftPreferences({
                  ...draftPreferences,
                  alertLeadHours: Number(event.detail.value),
                })}
              >
                {EXPIRATION_ALERT_LEAD_OPTIONS.map((hours) => (
                  <IonSelectOption key={hours} value={hours}>
                    {formatExpirationLead(hours)} prima
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <h2 style={{ fontSize: 16, margin: '24px 0 4px', color: palette.text }}>
              Notifiche sul telefono
            </h2>
            <p style={{ fontSize: 13, color: palette.textMuted, margin: '0 0 8px', lineHeight: 1.45 }}>
              Puoi ricevere fino a due promemoria. Scegli “Disattivata” in entrambi per non riceverne.
            </p>

            {[0, 1].map((index) => (
              <IonItem key={index}>
                <IonLabel position="stacked">{index === 0 ? 'Primo promemoria' : 'Secondo promemoria'}</IonLabel>
                <IonSelect
                  value={draftPreferences.reminderLeadHours[index] ?? 0}
                  interface="popover"
                  aria-label={index === 0 ? 'Anticipo primo promemoria' : 'Anticipo secondo promemoria'}
                  onIonChange={(event) => updateReminder(index, Number(event.detail.value))}
                >
                  <IonSelectOption value={0}>Disattivata</IonSelectOption>
                  {EXPIRATION_REMINDER_LEAD_OPTIONS.map((hours) => (
                    <IonSelectOption
                      key={hours}
                      value={hours}
                      disabled={draftPreferences.reminderLeadHours[index === 0 ? 1 : 0] === hours}
                    >
                      {formatExpirationLead(hours)} prima
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            ))}

            <p style={{ fontSize: 12, color: palette.textFaint, margin: '14px 0 0', lineHeight: 1.45 }}>
              TrackEAT riprogramma soltanto le notifiche scadenza del locale corrente; gli altri promemoria restano invariati.
            </p>
            <IonButton
              expand="block"
              style={{ marginTop: 24, minHeight: 48 }}
              onClick={() => void saveSettings()}
              disabled={savingSettings}
            >
              {savingSettings ? 'Salvataggio…' : 'Salva tempi'}
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}
